import React, { useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    SelectArrayInput,
    BooleanInput,
    required,
    minLength,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    SaveButton,
    DeleteButton,
    Toolbar,
    useRecordContext,
    DateField,
    usePermissions,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import {
    ArrowBack,
    Groups as GroupsIcon,
    Settings as SettingsIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Info,
} from '@mui/icons-material';
import { MembersTab } from '../members/MembersTab';
import { AssignmentsTab } from '../assignments/AssignmentsTab';
import { ServerErrorBinder } from '../../common/ServerErrorBinder';
import { translateServerMessage } from '../../../utils/translateServerMessage';

const NOTIFICATION_GROUP_KNOWN_FIELDS = new Set([
    'name', 'description', 'type', 'channels', 'active',
]);

const mapNotificationGroupBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const notificationGroupFieldLabelKey = (field: string): string =>
    NOTIFICATION_GROUP_KNOWN_FIELDS.has(field)
        ? `resources.notification-groups.fields.${field}`
        : field;

const EditToolbar = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const canDelete = permissions?.includes('notifications:manage:all');

    const recordId = String(record?.id || '');
    const isSystemGroup = recordId.startsWith('group_system_') ||
                         recordId.startsWith('group_payment_') ||
                         recordId.startsWith('group_operations_');

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
            {canDelete && !isSystemGroup && (
                <DeleteButton
                    confirmTitle={translate('resources.notification-groups.delete.confirmTitle')}
                    confirmContent={translate('resources.notification-groups.delete.confirmContent')}
                />
            )}
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
        }}>
            <IconButton
                onClick={() => redirect('/notification-groups')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.notification-groups.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.notification-groups.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {record?.name} • {record?.type}
                </Typography>
            </Box>
        </Box>
    );
};

const GroupEditContent = ({ serverErrors }: { serverErrors: Record<string, string> }) => {
    const translate = useTranslate();
    const [tabValue, setTabValue] = useState(0);
    const record = useRecordContext();
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const typeChoices = [
        { id: 'site', name: translate('resources.notification-groups.types.site') },
        { id: 'charger', name: translate('resources.notification-groups.types.charger') },
        { id: 'payment', name: translate('resources.notification-groups.types.payment') },
        { id: 'system', name: translate('resources.notification-groups.types.system') },
        { id: 'custom', name: translate('resources.notification-groups.types.custom') },
    ];

    const channelChoices = [
        { id: 'email', name: translate('resources.notification-groups.channels.email') },
        { id: 'sms', name: translate('resources.notification-groups.channels.sms') },
        { id: 'whatsapp', name: translate('resources.notification-groups.channels.whatsapp') },
        { id: 'push', name: translate('resources.notification-groups.channels.push') },
        { id: 'slack', name: translate('resources.notification-groups.channels.slack') },
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <SimpleForm
            toolbar={<EditToolbar />}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
            }}
        >
            <ServerErrorBinder errors={serverErrors} />
            <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                <PageHeader />

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant={isMobile ? "fullWidth" : "scrollable"}
                            scrollButtons={isMobile ? false : "auto"}
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: { xs: 40, sm: 48 },
                                '& .MuiTab-root': {
                                    minWidth: { xs: 0, sm: 90 },
                                    minHeight: { xs: 40, sm: 48 },
                                    px: { xs: 0.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1.5 },
                                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                    lineHeight: 1.1,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                },
                            }}
                        >
                            <Tab
                                icon={<SettingsIcon sx={{ fontSize: 20 }} />}
                                iconPosition="start"
                                label={translate('resources.notification-groups.tabs.general')}
                            />
                            <Tab
                                icon={<PeopleIcon sx={{ fontSize: 20 }} />}
                                iconPosition="start"
                                label={translate('resources.notification-groups.tabs.members')}
                            />
                            <Tab
                                icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
                                iconPosition="start"
                                label={translate('resources.notification-groups.tabs.assignments')}
                            />
                        </Tabs>
                    </Box>

                    {tabValue === 0 && (
                        <Box sx={{ mt: 3 }}>
                            {/* Basic Information */}
                            <Card sx={{ mb: 3, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                <GroupsIcon sx={{ mr: 2, color: '#00C853' }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {translate('resources.notification-groups.sections.basicInfo')}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <TextInput
                                                    source="name"
                                                    label="resources.notification-groups.fields.name"
                                                    validate={[required(), minLength(2), maxLength(100)]}
                                                    fullWidth
                                                    helperText="resources.notification-groups.help.name"
                                                />
                                                <SelectInput
                                                    source="type"
                                                    label="resources.notification-groups.fields.type"
                                                    choices={typeChoices}
                                                    validate={[required()]}
                                                    fullWidth
                                                    helperText="resources.notification-groups.help.type"
                                                />
                                                <TextInput
                                                    source="description"
                                                    label="resources.notification-groups.fields.description"
                                                    multiline
                                                    rows={3}
                                                    fullWidth
                                                    helperText="resources.notification-groups.help.description"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Channels Configuration */}
                                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                <SettingsIcon sx={{ mr: 2, color: '#00C853' }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {translate('resources.notification-groups.sections.channelsConfig')}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <SelectArrayInput
                                                    source="channels"
                                                    label="resources.notification-groups.fields.channels"
                                                    choices={channelChoices}
                                                    validate={[required()]}
                                                    fullWidth
                                                    helperText="resources.notification-groups.help.channels"
                                                />
                                                <BooleanInput
                                                    source="active"
                                                    label="resources.notification-groups.fields.active"
                                                    helperText="resources.notification-groups.help.active"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Metadata */}
                                    <Card sx={{ overflow: 'hidden' }}>
                                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {translate('resources.notification-groups.sections.metadata')}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                                <DateField
                                                    source="createdAt"
                                                    label="resources.notification-groups.fields.createdAt"
                                                    showTime
                                                />
                                                <DateField
                                                    source="updatedAt"
                                                    label="resources.notification-groups.fields.updatedAt"
                                                    showTime
                                                />
                                            </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Box sx={{ mt: 3 }}>
                            <MembersTab />
                        </Box>
                    )}

                    {tabValue === 2 && (
                        <Box sx={{ mt: 3 }}>
                            <AssignmentsTab groupId={String(record?.id || '')} />
                        </Box>
                    )}
                </Box>
            </Box>
        </SimpleForm>
    );
};

export const GroupEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    return (
        <Edit
            actions={false}
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.notification-groups.notifications.updateSuccess', { type: 'success' });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapNotificationGroupBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(notificationGroupFieldLabelKey(field), { _: field });
                            summaryLines.push(`\u2022 ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <GroupEditContent serverErrors={serverErrors} />
        </Edit>
    );
};
